import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { PLATFORMS, usePlatform } from '@hmhco/platform-helper';
import { getPkceCodeVerifier } from '@hmh/eds-login-js';
import useGetCachedOrgInfo from './getCachedOrgInfo.hook';
import {
  useGetConnectionFromStorage,
  useGetConnectionFromUrl,
} from './getConnectionParam.hook';
import { setCachedOrgInfo } from '../store/actions/CountryStateOrgActions';
import useSetUrlParamsInStorage from './setUrlParamsInStorage';
import {
  setCachedOktaWidgetConfig,
  setLoginFlow,
  setLoginStep,
} from '../store/actions/TenantPickerActions';
import { setOktaConfig } from '../storageHelpers/sessionStorageAccessors';
import { LOGIN_FLOWS } from '../constants/constants';
import { useLoginHint } from './useLoginHint.hook';
import { handleLoginHint } from '../util/handleLoginHint';
import { handleOrgInfo } from '../util/handleOrgInfo';
import useCallbackParams from './useCallbackParams.hook';
import { authorize } from '../util/authorize';

function useBootstrapTenantPicker() {
  const dispatch = useDispatch();
  const platform = usePlatform();
  const connectionFromUrl = useGetConnectionFromUrl();
  const connectionFromStorage = useGetConnectionFromStorage();
  const pid = connectionFromUrl || connectionFromStorage;
  const isPidFromUrl = pid === connectionFromUrl;
  const { allKeysHaveValue, params } = useCallbackParams();
  const cachedOrgInfo = useGetCachedOrgInfo(pid);
  const loginHint = useLoginHint();
  useSetUrlParamsInStorage();
  const search = new URLSearchParams(window.location.search);
  const resourceId = search.get('resource_id') || '';
  const resourceUrl = search.get('resource_url') || '';

  useEffect(() => {
    if (search.has('iss') && !search.has('client_id')) {
      return;
    }

    if (loginHint && cachedOrgInfo) {
      handleLoginHint(dispatch, pid, cachedOrgInfo);
      return;
    }

    if (params && allKeysHaveValue) {
      if (platform === PLATFORMS.NWEA) {
        const isSecure = !!window.location.protocol.match('https:?');
        const pkceCodeChallengeMethod = isSecure ? 'S256' : 'plain';

        (async () => {
          const { challenge, verifier } = await getPkceCodeVerifier(
            pkceCodeChallengeMethod,
          );
          sessionStorage.setItem('verifier', verifier);
          sessionStorage.setItem('challenge', challenge);

          setOktaConfig(JSON.stringify(params));
          dispatch(setLoginStep(2));
          dispatch(setLoginFlow(LOGIN_FLOWS.OKTA));
          dispatch(setCachedOktaWidgetConfig(params));
        })();
      } else {
        setOktaConfig(JSON.stringify(params));
        dispatch(setLoginStep(2));
        dispatch(setLoginFlow(LOGIN_FLOWS.OKTA));
        dispatch(setCachedOktaWidgetConfig(params));
        dispatch(setCachedOrgInfo(cachedOrgInfo));
      }
      return;
    }

    if (pid) {
      handleOrgInfo(
        dispatch,
        pid,
        isPidFromUrl,
        resourceId,
        resourceUrl,
        platform,
      );
      return;
    }

    if (cachedOrgInfo) {
      dispatch(setCachedOrgInfo(cachedOrgInfo));
      dispatch(setLoginFlow(2));
      authorize({
        pid,
        loginHint: cachedOrgInfo.data.org.orgRefId,
        ...(resourceId ? { resourceId } : {}),
        ...(resourceUrl ? { resourceUrl } : {}),
        isPidFromUrl: false,
        platform,
      });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
}

export default useBootstrapTenantPicker;
